import { hasOwnPropertyGuard } from '../../../core/common/helpers/objects/has-own-property-guard.helper';
import { FormValue } from '../../../form-controls/entities/form-value.entity';
import {
    ComplexFieldValue,
    ComplexListFieldValue,
    ComplexMapFieldValue,
    ListFieldValue,
    MapFieldValue,
    MultiItemFieldValue,
    NonNullComplexFieldValue,
    NonNullComplexListFieldValue,
    NonNullListFieldValue,
    NonNullMapFieldValue,
    NonNullMultiItemFieldValue,
    NonNullSimpleFieldValue,
    SimpleFieldValue,
    ThesaurusFieldValue
} from '../../entities/field-values/field-type-values.entity';

export function isSimpleFieldValue(x: any): x is SimpleFieldValue {
    return isString(x) || isNumber(x) || isBoolean(x) || isNull(x);
}

export function isNonNullSimpleFieldValue(x: any): x is NonNullSimpleFieldValue {
    return !isNull(x) && isSimpleFieldValue(x);
}

export function assertSimpleFieldValue(x: any): asserts x is SimpleFieldValue {
    if (!isSimpleFieldValue(x)) {
        throw new Error(`argument ${JSON.stringify(x)} is not a valid SimpleFieldValue`);
    }
}

export function assertRecordOfSimpleFieldValueArray(x: unknown): asserts x is ListFieldValue | MultiItemFieldValue {
    assertObject(x);
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            assertString(prop);
            const val: any = x[prop];
            if (!Array.isArray(val)) {
                throw new Error(`argument ${JSON.stringify(x)} is not a pure data Object`);
            }
            val.forEach(el => assertSimpleFieldValue(el));
        } else {
            throw new Error(`argument ${JSON.stringify(x)} is not a pure data Object`);
        }
    }
}

export function isRecordOfSimpleFieldValueArray(x: unknown): x is ListFieldValue | MultiItemFieldValue {
    if (!isObject(x)) {
        return false;
    }
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            if (!isString(prop)) {
                return false;
            }
            const val: any = x[prop];
            if (!Array.isArray(val)) {
                return false;
            }
            return val.every(el => isSimpleFieldValue(el));
        } else {
            return false;
        }
    }
    return true;
}

export function isRecordOfSimpleFieldValueArrayWithValues(
    x: unknown
): x is NonNullMultiItemFieldValue | NonNullListFieldValue {
    if (!isObject(x)) {
        return false;
    }
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            if (!isString(prop)) {
                return false;
            }
            const val: any = x[prop];
            if (!Array.isArray(val) || val.length === 0) {
                return false;
            }
            return val.every(el => isNonNullSimpleFieldValue(el));
        } else {
            return false;
        }
    }
    return true;
}

export function isRecordOfComplexTypeArray(x: unknown): x is ComplexFieldValue | ComplexListFieldValue {
    if (!isObject(x)) {
        return false;
    }
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            const arr: any = x[prop];

            if (Array.isArray(arr)) {
                return arr.every(el => isObject(el) || isNonNullSimpleFieldValue(el));
            }
        } else {
            return false;
        }
    }
    return true;
}

export function isRecordOfComplexTypeWithValues(
    x: unknown
): x is NonNullComplexFieldValue | NonNullComplexListFieldValue {
    if (!isObject(x)) {
        return false;
    }
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            const val: any = x[prop];

            if (!isObject(val)) {
                return false;
            }
            if (!Array.isArray(val) || val.length === 0) {
                return false;
            }

            return val.every(
                el =>
                    isRecordOfSimpleFieldValueWithValues(el) ||
                    isRecordOfSimpleFieldValueArrayWithValues(el) ||
                    isThesaurusFieldWithValues(el)
            );
        } else {
            return false;
        }
    }
    return true;
}

export function isRecordOfComplexType(x: unknown): x is ComplexMapFieldValue {
    if (!isObject(x)) {
        return false;
    }
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            const el: any = x[prop];

            if (Array.isArray(el)) {
                return false;
            }

            return isObject(el);
        } else {
            return false;
        }
    }
    return true;
}

export function isThesaurusField(x: unknown): x is ThesaurusFieldValue {
    if (x === null) {
        return true;
    }

    if (!isObject(x)) {
        return false;
    }

    const objectKeys: string[] = Object.keys(x);
    const hasUriProperty: boolean = hasOwnPropertyGuard(x, 'Uri');
    const hasMoreThanOneObjectKey: boolean = objectKeys.length > 1;
    const everyPropertyIsAString: boolean = Object.values(x).every(value => isString(value));

    return hasUriProperty && hasMoreThanOneObjectKey && everyPropertyIsAString;
}

export function isThesaurusFieldWithValues(x: unknown): x is NonNullComplexFieldValue | NonNullComplexListFieldValue {
    if (!isObject(x)) {
        return false;
    }

    const objectKeys: string[] = Object.keys(x);
    const hasUriProperty: boolean = hasOwnPropertyGuard(x, 'Uri');
    const hasMoreThanOneObjectKey: boolean = objectKeys.length > 1;
    const everyPropertyIsAString: boolean = Object.values(x).every(value => isString(value));

    return hasUriProperty && hasMoreThanOneObjectKey && everyPropertyIsAString;
}

export function isRecordOfSimpleFieldValue(x: unknown): x is MapFieldValue {
    if (!isObject(x)) {
        return false;
    }
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            const condition: boolean = isString(prop) && isSimpleFieldValue(x[prop]);
            if (!condition) {
                return false;
            }
        } else {
            return false;
        }
    }
    return true;
}

export function isRecordOfSimpleFieldValueWithValues(x: unknown): x is NonNullMapFieldValue {
    if (!isObject(x)) {
        return false;
    }
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            const condition: boolean = isString(prop) && isNonNullSimpleFieldValue(x[prop]);
            if (!condition) {
                return false;
            }
        } else {
            return false;
        }
    }
    return true;
}

export function isRecordOfRecordOfSimpleFieldValueArray(x: unknown): x is ComplexFieldValue {
    if (!isObject(x)) {
        return false;
    }
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            if (!isString(prop)) {
                return false;
            }
            const val: any = x[prop];
            if (!Array.isArray(val)) {
                return false;
            }

            return val.every(el => isRecordOfSimpleFieldValue(el));
        } else {
            return false;
        }
    }
    return true;
}

export function assertRecordOfSimpleFieldValue(x: unknown): asserts x is MapFieldValue {
    assertObject(x);
    for (const prop in x) {
        if (hasOwnPropertyGuard(x, prop)) {
            assertString(prop);
            assertSimpleFieldValue(x[prop]);
        } else {
            throw new Error(`argument ${JSON.stringify(x)} is not a pure data Object`);
        }
    }
}

export function isNumber(x: unknown): x is number {
    return typeof x === 'number';
}

export function assertNumber(x: unknown): asserts x is number {
    if (!isNumber(x)) {
        throw new Error(`argument "${x}" is not a valid number`);
    }
}

export function assertNumberOrNull(x: unknown): asserts x is number | null {
    if (!isNumber(x) && !isNull(x)) {
        throw new Error(`argument "${x}" is not a valid number or null`);
    }
}

export function isObject(x: unknown): x is object {
    return typeof x === 'object' && !isNull(x) && !isDate(x);
}

export function isNull(x: unknown): x is null {
    return x === null;
}

export function isString(x: unknown): x is string {
    return typeof x === 'string';
}

export function isDate(x: unknown): x is Date {
    return Object.prototype.toString.call(x) === '[object Date]';
}

export function assertObject(x: unknown): asserts x is object {
    if (!isObject(x)) {
        throw new Error(`argument ${JSON.stringify(x)} is not a valid object`);
    }
}

export function assertString(x: unknown): asserts x is string {
    if (!isString(x)) {
        throw new Error(`argument "${JSON.stringify(x)}" is not a valid string`);
    }
}

export function assertDate(x: unknown): asserts x is Date {
    if (!isDate(x)) {
        throw new Error(`argument "${JSON.stringify(x)}" is not a valid Date`);
    }
}

export function assertStringOrNull(x: unknown): asserts x is string | null {
    if (!isString(x) && !isNull(x)) {
        throw new Error(`argument "${JSON.stringify(x)}" is not a valid string or null`);
    }
}

export function assertThesaurusFieldOrNull(x: unknown): asserts x is ThesaurusFieldValue {
    if (!isThesaurusField(x)) {
        throw new Error(`argument "${JSON.stringify(x)}" is not a thesaurus field`);
    }
}

export function assertDateOrNull(x: unknown): asserts x is Date | null {
    if (!isDate(x) && !isNull(x)) {
        throw new Error(`argument "${JSON.stringify(x)}" is not a valid Date or null`);
    }
}

export function assertFormValueStringOrNull(x: FormValue<unknown>): asserts x is FormValue<string | null> {
    return assertStringOrNull(x.value);
}

export function assertFormValueBooleanOrNull(x: FormValue<unknown>): asserts x is FormValue<boolean | null> {
    return assertBooleanOrNull(x.value);
}

export function assertFormValueNumberOrNull(x: FormValue<unknown>): asserts x is FormValue<number | null> {
    return assertNumberOrNull(x.value);
}

export function isBoolean(x: unknown): x is boolean {
    return typeof x === 'boolean';
}

export function assertBoolean(x: unknown): asserts x is boolean {
    if (!isBoolean(x)) {
        throw new Error(`argument "${JSON.stringify(x)}" is not a valid boolean`);
    }
}

export function assertBooleanOrNull(x: unknown): asserts x is boolean | null {
    if (!isBoolean(x) && !isNull(x)) {
        throw new Error(`argument "${JSON.stringify(x)}" is not a valid boolean or null`);
    }
}
